import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { rankingList } from '@/mocks/ranking';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/categories';
import RankingList from '@/components/feature/RankingList';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const DOMAIN = 'https://jianxiaoer.cn';

export default function RankingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [activeCat, setActiveCat] = useState<string>('all');

  useSEO({
    title: '全网热搜榜 - 实时热议排行与突发热度追踪 | 爆料前线',
    description:
      '爆料前线全网热搜榜每 15 分钟刷新一次，汇总当前热度最高的热议排行与突发新闻，并标注排名升降与最新上榜话题，实时看清事件冲到哪一步。',
    keywords: '全网热搜,热议排行,实时热点,热搜榜单,突发新闻',
    path: '/ranking',
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '全网热搜榜',
        url: `${DOMAIN}/ranking`,
        inLanguage: 'zh-CN',
        description: '实时汇总热度最高的热议排行与突发新闻话题。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${DOMAIN}/` },
          { '@type': 'ListItem', position: 2, name: '热搜榜', item: `${DOMAIN}/ranking` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '全网热搜榜 TOP 12',
        itemListElement: rankingList.map((item) => ({
          '@type': 'ListItem',
          position: item.rank,
          url: `${DOMAIN}/topic/${item.slug}`,
          name: item.title,
        })),
      },
    ],
  });

  const filtered = useMemo(() => {
    return rankingList.filter((item) => {
      const topic = topics.find((t) => t.slug === item.slug);
      if (activeCat !== 'all' && (!topic || topic.categorySlug !== activeCat)) return false;
      if (keyword && !item.title.includes(keyword)) return false;
      return true;
    });
  }, [activeCat, keyword]);

  const clearKeyword = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    setSearchParams(next);
  };

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '热搜榜' }]} />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-bar-chart-2-line text-sm" />
            </span>
            每 15 分钟刷新 · 数据来自站内外讨论量综合统计
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">全网热搜榜</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            这里汇总当前热度最高的热议排行，右侧标注排名升降与最新上榜情况。想看事件细节，直接点开标题进入话题时间线即可。
          </p>

          <div className="mt-7 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveCat('all')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  activeCat === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                }`}
              >
                全部频道
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setActiveCat(cat.slug)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {keyword && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-700">
                <span className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                    <i className="ri-search-line text-base" />
                  </span>
                  搜索关键词：<strong className="text-foreground-950">{keyword}</strong>
                </span>
                <span className="text-foreground-500">命中 {filtered.length} 条结果</span>
                <button
                  type="button"
                  onClick={clearKeyword}
                  className="cursor-pointer whitespace-nowrap text-primary-700 transition-colors hover:text-primary-600"
                >
                  清除筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-2 md:px-6">
            {filtered.length > 0 ? (
              <RankingList items={filtered} />
            ) : (
              <div className="flex flex-col items-center gap-3 py-14 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-emotion-unhappy-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-700">当前筛选条件下没有匹配的话题，换个关键词或频道试试。</p>
                <button
                  type="button"
                  onClick={() => {
                    setActiveCat('all');
                    clearKeyword();
                  }}
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>

          <p className="mt-5 text-xs leading-relaxed text-foreground-500">
            热度值由站内阅读、评论与外部讨论量综合计算，仅反映讨论规模，不代表平台对事件本身的判断。
          </p>
        </div>
      </section>
    </div>
  );
}